import { fitInRectTransform, Transform } from "../../../transform";
import type { IAdornerImageSource } from "../slideshow-preview-container";

import "./adorner-renderer.css";

export interface IAdornerRendererDeps {
	createAdornerImageAsync: (
		adornerImage: eXpress.core.IAdornerImage,
	) => Promise<IAdornerImageSource>;
}

interface IAdornerRendererResources {
	imageEl: HTMLImageElement;
	adorner: eXpress.core.IAdorner | null | undefined;
	dipToRenderScale: number;
}

export function createAdornerRenderer(deps: IAdornerRendererDeps) {
	// Don't use DIP image size, but a reduced size (iOS did crash when using too large images)
	// I scale image from DIP to 1500 because mostly adorners have max side of 1500px
	const maxImageSide = 1500;
	let adornerPreviewToAdornerTransform = Transform.unit;

	const { createAdornerImageAsync } = deps;
	let renderSize = eXpress.math.Vector.zero;
	/** Remember adorner requested (so available before resources are loaded) */
	let requestedAdorner: eXpress.core.IAdorner | undefined;

	// Create a div containing the image
	const el = document.createElement("div");
	el.classList.add("adorner");

	/** Load the required resources to synchroniously render the adorner */
	async function getRenderResourcesAsync(
		adorner: eXpress.core.IAdorner | null | undefined,
	): Promise<IAdornerRendererResources | undefined> {
		requestedAdorner = adorner || undefined;
		if (!adorner) return undefined;

		const dipToRenderScale =
			adorner.image.width > adorner.image.height
				? maxImageSide / adorner.image.width
				: maxImageSide / adorner.image.height;

		// Currently unit transform:
		// We will draw the image in DIP size so the transform will not change when the adorner image is loaded (other dimensions as DIP, not known upfront)
		// This makes the scenegraph stable and fixes some errors when calculating a minimum adorner viewBox for an adorner image not yet loaded
		adornerPreviewToAdornerTransform = Transform.unit;

		// Because we use an image (instead of canvas), the loading of the image is also part of the async stuff
		const source = await createAdornerImageAsync(adorner.image);
		const imageEl = await eXpress.platform.image
			.loadImageAsync(source.uri, undefined)
			.promise();

		return {
			imageEl,
			adorner,
			dipToRenderScale,
		};
	}

	/** Accepts the result of getRenderResourcesAsync */
	function renderAdorner(renderResources: IAdornerRendererResources | undefined) {
		const { imageEl, adorner, dipToRenderScale } = renderResources ?? {};
		if (requestedAdorner !== adorner) return; // Only render what is last requested (respect order of promises)

		const isBackground = !!renderResources?.adorner?.image.isBackground;
		let currentImageEl = el.firstChild as HTMLImageElement | null; // Cast because we know there should only be 1 img element

		if (imageEl && adorner && dipToRenderScale) {
			el.classList.toggle("adorner--is-background", isBackground);

			if (!currentImageEl) {
				currentImageEl = imageEl;
				el.appendChild(imageEl);
			}

			renderSize = adorner
				? eXpress.math
						.vector(adorner.image.width, adorner.image.height)
						.scale(dipToRenderScale)
				: eXpress.math.Vector.zero;

			// Don't do anything if it has already the correct image
			if (imageEl.src !== currentImageEl.src) {
				// Update adorner image
				currentImageEl.src = imageEl.src;
			}

			currentImageEl.width = renderSize.width;
			currentImageEl.height = renderSize.height;
			currentImageEl.style.transformOrigin = "0 0";
			currentImageEl.style.transform = `scale(${1 / dipToRenderScale})`;
			// add draggable attribute to disable the longpress on iOS for the adorner.
			currentImageEl.setAttribute("draggable", "false");
		} else {
			// Remove adorner
			el.classList.remove("adorner--is-background");
			currentImageEl?.remove();
			renderSize = eXpress.math.Vector.zero;
		}
	}

	/** Size of the adorner on screen (Can be different as the DIP Adorner image size) */
	function getRenderSize() {
		return renderSize;
	}

	/** Apply transform on adorner with a transform */
	function applyTransform(transform: Transform) {
		transform.setCssTransform(el);
	}

	/** The transform from the rendered adorner on screen to the Adorner DIP */
	function getRenderToAdornerTransform() {
		return adornerPreviewToAdornerTransform;
	}

	/** The transform from the rendered adorner on screen to fit/fill an image with the specified size in the projection box */
	function getRenderToProjectionTransform(spreadSize: eXpress.math.IVector) {
		const adorner = requestedAdorner;
		if (!adorner) return Transform.unit;

		const adornerRenderProjectionBox = getRenderToAdornerTransform()
			.inverse()
			.transformRect(adorner.projection);

		// shouldn't we better 'fill' spread in adorner projection?
		const spreadToAdornerRenderTransform = fitInRectTransform(
			spreadSize,
			adornerRenderProjectionBox,
		);

		// Should we log mismatches in spread vs adorner projection aspect ratios
		return new Transform(spreadToAdornerRenderTransform).inverse();
	}

	return {
		get el() {
			return el;
		}, // Use getter so DOM element can be collected if parent doesn't use a direct reference
		getRenderResourcesAsync,
		renderAdorner,
		getRenderSize,
		applyTransform,
		getRenderToProjectionTransform,
		getRenderToAdornerTransform,
	};
}

export type IAdornerRenderer = ReturnType<typeof createAdornerRenderer>;
